import { SSE } from 'sse.js'
import { getSessionUser } from '../api'
import { addFieldsForHybridX } from 'app-api-user-service'
import { getCurrentLocale, getCookie } from '@insight/toolkit-utils/lib/helpers'
import { INSIGHT_CURRENT_LOCALE_COOKIE_NAME, IPS_CONTRACT_NAME_COOKIE_NAME, IPS_CONTRACT_ID_COOKIE_NAME } from '../constants'
import { getDefaultCurrencyByLocale } from '../shared/utils'

const PRICE_SSE_ENDPOINT = '/gapi/product-search/pricing-sse'
const currentLocale = getCurrentLocale(INSIGHT_CURRENT_LOCALE_COOKIE_NAME)
const logoutContractId = getCookie(IPS_CONTRACT_ID_COOKIE_NAME)
const safeDecodeURIComponent = (value) => {
   try {
     return decodeURIComponent(value || '')
   } catch (_) {
     return ''
   }
 }
 const logoutContractType = safeDecodeURIComponent(getCookie(IPS_CONTRACT_NAME_COOKIE_NAME))
 
const toPositiveInteger = (value) => {
  const numericValue = Number(value)
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : undefined
}

/**
 * Fetches async prices for a list of materialIds via the existing SSE endpoint.
 *
 * The endpoint streams a `prices` event whose payload is:
 *   { [materialId]: priceObject }
 *
 * Prices are delivered incrementally as the stream progresses.
 *
 * @param {object}   options
 * @param {string[]} options.materialIds  - List of material IDs from Coveo results
 * @param {string}   options.salesOrg     - Sales org for pricing context
 * @param {string}   [options.contractId]   - IPS contract number (if applicable)
 * @param {string}   [options.contractType] - IPS contract type (if applicable)
 * @param {Function} options.onPrices     - Called each time a prices chunk arrives: (priceChunk) => void
 * @param {Function} options.onComplete   - Called when the SSE stream closes cleanly
 * @param {Function} options.onError      - Called on SSE error
 * @returns {Function} A cancel() function to abort the SSE connection early
 */
export async function fetchSearchPrices({
  materialIds = [],
  salesOrg,
  contractId,
  contractType,
  onPrices,
  onComplete,
  onError,
}) {
  if (!materialIds.length) {
    onComplete?.()
    return () => {}
  }

  const flattenMaterialIds = (ids, acc = []) => {
    ids.forEach((id) => {
      if (Array.isArray(id)) {
        flattenMaterialIds(id, acc)
      } else {
        acc.push(id)
      }
    })
    return acc
  }

  const normalizedMaterialIds = flattenMaterialIds(Array.isArray(materialIds) ? materialIds : [materialIds])
    .map((id) => (id != null ? `${id}` : ''))
    .filter(Boolean)
    .map((id) => id.toUpperCase())

  const normalizedSalesOrg = salesOrg != null ? `${salesOrg}`.trim() : ''

  if (!normalizedSalesOrg) {
    onError?.()
    onComplete?.()
    return () => {}
  }

  if (!normalizedMaterialIds.length) {
    onComplete?.()
    return () => {}
  }

  // Resolve user context needed to authenticate the price request
  const {
    isLoggedIn,
    isIpsLogo,
    locale: sessionLocale,
    userInformation: {
      isCES,
      currencyCode,
      webGroup: { webGroupId: webgroup } = {},
      account: { soldToId: soldTo } = {},
      isIpsUser: loginIpsUser,
      webLoginProfileId: userId,
      UserType: userType,
      contract
    } = {},
  } = await getSessionUser().catch(() => ({}))

  const isIpsUser = loginIpsUser === true || isIpsLogo === true

  // Logged-out sessions have no userInformation.currencyCode, so derive it from locale instead of hardcoding USD
  const resolvedCurrencyCode = currencyCode || getDefaultCurrencyByLocale(sessionLocale || currentLocale)

  // userId, webGroup and soldTo must all be positive integers when any is provided
  const normalizedSoldTo = toPositiveInteger(soldTo)
  const normalizedWebgroup = toPositiveInteger(webgroup)
  const normalizedUserId = toPositiveInteger(userId)

  const payload = {
    // Tell the backend we only need prices, not a full product search.
    // Using materialIds as the filter so the backend can scope pricing.
    // materialIds: [String]
    // salesOrg: String
    // currencyCode: String
    // locale: String
    // soldTo: String
    // webgroup: String
    // contractId: String
    // selectedContractIDs: [String]
    // chunkSize: Number
    materialIds: normalizedMaterialIds,
    salesOrg: normalizedSalesOrg,
    currencyCode: resolvedCurrencyCode,
    chunkSize: 10,
    locale: sessionLocale || currentLocale
  }

  if(isIpsUser) {
    if(!contract) {
      // Logged-out IPS user: only send contractId if one was selected (saved in cookie)
      // otherwise don't send any contractId in the request payload
      if (logoutContractId && Number(logoutContractId) !== 0) {
        payload.contractId = logoutContractId
      }
      payload.contractType = logoutContractType || 'openMarket'
    } else {
      const {contractId, contractType, name} = contract
      const IPSContractType = contractType === 'Open market' ? 'openMarket' : name || contractType
      const IPSContractId = ((contractId || name) === 'Open market') ? null : contractId
      payload.contractId = IPSContractId
      payload.contractType = IPSContractType
    }
    
  }

  if (isLoggedIn && normalizedSoldTo && normalizedWebgroup) {
    if (normalizedUserId) {
      payload.userId = normalizedUserId
    }
    payload.soldTo = normalizedSoldTo
    payload.webGroup = normalizedWebgroup
    await addFieldsForHybridX({ isLoggedIn, isCES }, payload, { userType }).catch((error) => {
      onError?.(error)
    })
  } else if (normalizedUserId) {
    payload.userId = normalizedUserId
  }

  const MAX_RETRIES = 2
  let retryCount = 0
  let source

  const connect = () => {
    source = new SSE(PRICE_SSE_ENDPOINT, {
      headers: { 'Content-Type': 'application/json', charset: 'utf-8' },
      method: 'POST',
      payload: JSON.stringify(payload),
    })

    source.addEventListener('readystatechange', (e) => {
      if (e.readyState === 2) {
        onComplete?.()
      }
    })

    source.addEventListener('prices', (e) => {
      try {
        const chunk = JSON.parse(e.data)
        onPrices?.(chunk)
      } catch (error) {
        onError?.(error)
      }
    })

    source.addEventListener('error', () => {
      source?.close?.()
      if (retryCount < MAX_RETRIES) {
        retryCount += 1
        connect()
      } else {
        onError?.()
      }
    })
  }

  connect()

  // Return a cancel function so callers can abort on unmount / new search
  return () => source.close?.()
}
